import { createJsonMemoryAccessors } from '@/services/storage'

import type { IntegrationConfig, IntegrationHealth, IntegrationLog } from '../entities'
import { DEFAULT_INTEGRATION_CONFIG } from '../entities'

const LOGS_KEY = 'masterhub.integrations.logs.v1'
const HEALTH_KEY = 'masterhub.integrations.health.v1'
const CONFIG_KEY = 'masterhub.integrations.config.v1'

type Slot = 'logs' | 'health' | 'config'

const memory: Record<Slot, unknown> = {
  logs: null,
  health: null,
  config: null,
}

const { readJson, writeJson } = createJsonMemoryAccessors(memory)

export function resetIntegrationMemory(): void {
  memory.logs = null
  memory.health = null
  memory.config = null
}

export class IntegrationLogRepository {
  list(supplierId?: string): IntegrationLog[] {
    const all = readJson<IntegrationLog[]>(LOGS_KEY, 'logs', [])
    if (!supplierId) return all
    return all.filter((l) => l.supplierId === supplierId)
  }

  add(entry: Omit<IntegrationLog, 'id' | 'createdAt'>): IntegrationLog {
    const log: IntegrationLog = {
      ...entry,
      id: crypto.randomUUID(),
      createdAt: new Date().toISOString(),
    }
    writeJson(LOGS_KEY, 'logs', [log, ...this.list()].slice(0, 500))
    return log
  }
}

export class IntegrationHealthRepository {
  list(): IntegrationHealth[] {
    return readJson<IntegrationHealth[]>(HEALTH_KEY, 'health', [])
  }

  upsert(health: IntegrationHealth): IntegrationHealth {
    const list = this.list()
    const index = list.findIndex((h) => h.supplierId === health.supplierId)
    if (index >= 0) list[index] = health
    else list.push(health)
    writeJson(HEALTH_KEY, 'health', list)
    return health
  }

  get(supplierId: string): IntegrationHealth | null {
    return this.list().find((h) => h.supplierId === supplierId) ?? null
  }
}

export class IntegrationConfigRepository {
  get(): IntegrationConfig {
    return readJson(CONFIG_KEY, 'config', DEFAULT_INTEGRATION_CONFIG)
  }

  save(patch: Partial<IntegrationConfig>): IntegrationConfig {
    const next = { ...this.get(), ...patch }
    writeJson(CONFIG_KEY, 'config', next)
    return next
  }
}

export const integrationLogRepository = new IntegrationLogRepository()
export const integrationHealthRepository = new IntegrationHealthRepository()
export const integrationConfigRepository = new IntegrationConfigRepository()
